import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Hub } from "aws-amplify/utils";
import { signOut } from "aws-amplify/auth";
import {
  ACCOUNT_CONTEXT_INVALID_EVENT,
  clearSelectedAccountId,
} from "../../shared/lib/auth/accountContext";
import {
  clearSessionProfileState,
  clearLogoutSessionCache,
  clearSignInSessionCache,
  markSessionAuthenticated,
  markSessionUnauthenticated,
} from "../auth/authSessionState";
import { getAuthSessionUser } from "../auth/getAuthSessionUser";
import { getLastKnownAuth } from "../auth/authStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    getLastKnownAuth()
  );

  const [status, setStatus] = useState("booting");
  const [user, setUser] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function validateSession() {
      try {
        const builtUser = await getAuthSessionUser({ forceRefresh: false });

        if (!isMounted) return;

        if (builtUser) {
          setIsAuthenticated(true);
          setUser(builtUser);
          markSessionAuthenticated(builtUser);
        } else {
          setIsAuthenticated(false);
          setUser(null);
          markSessionUnauthenticated({ clearPlan: true });
        }
      } catch {
        if (!isMounted) return;

        setIsAuthenticated(false);
        setUser(null);
        markSessionUnauthenticated({ clearPlan: true });
      } finally {
        if (isMounted) {
          setStatus("ready");
        }
      }
    }

    validateSession();

    const handleAccountContextInvalid = () => {
      setUser((currentUser) => {
        if (!currentUser) return currentUser;

        clearSelectedAccountId(currentUser.email);

        const nextUser = {
          ...currentUser,
          accountId: null,
          accountSelectionRequired: true,
        };

        markSessionAuthenticated(nextUser);
        return nextUser;
      });
    };

    window.addEventListener(
      ACCOUNT_CONTEXT_INVALID_EVENT,
      handleAccountContextInvalid
    );

    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      const event = payload?.event;

      if (event === "signedIn") {
        try {
          clearSignInSessionCache();
          const builtUser = await getAuthSessionUser();
          setIsAuthenticated(true);
          setUser(builtUser);
          markSessionAuthenticated(builtUser);
        } catch {
          setUser(null);
          clearSessionProfileState();
        }
        setStatus("ready");
      }

      if (event === "signedOut") {
        setIsAuthenticated(false);
        setUser(null);
        markSessionUnauthenticated({ clearPlan: true });
        setStatus("ready");
      }
    });

    return () => {
      isMounted = false;
      window.removeEventListener(
        ACCOUNT_CONTEXT_INVALID_EVENT,
        handleAccountContextInvalid
      );
      unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      clearLogoutSessionCache();
      await signOut();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      throw error;
    }
  };

  const refreshUser = useCallback(async (forceRefresh = true) => {
    try {
      const builtUser = await getAuthSessionUser({ forceRefresh });

      if (builtUser) {
        setIsAuthenticated(true);
        setUser(builtUser);
        markSessionAuthenticated(builtUser);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        markSessionUnauthenticated();
      }

      return builtUser;
    } catch (error) {
      console.error("Erro ao atualizar sessão do usuário:", error);
      return null;
    }
  }, []);

  const updateAuthenticatedUser = useCallback((nextUser) => {
    if (!nextUser) return null;

    setIsAuthenticated(true);
    setUser(nextUser);
    markSessionAuthenticated(nextUser);

    return nextUser;
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      status,
      user,
      refreshUser,
      updateAuthenticatedUser,
      logout,
    }),
    [isAuthenticated, status, user, refreshUser, updateAuthenticatedUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
